import useFetch from 'react-fetch-hook';
import Order from './Order';
import viewModel from './ordersViewModel';
import { useState, useEffect, useMemo } from 'react';
import { Wrapper } from './ordersStyles';

// Removed unused API key; backend should handle authentication if required.

function useOrders() {
  // Cache orders per (limit,page) to avoid flicker and enable instant navigation
  const [ordersByKey, setOrdersByKey] = useState({});
  const [shownPage, setShownPage] = useState(1);
  const [shownLimit, setShownLimit] = useState(() => {
    const saved = parseInt(localStorage.getItem('pageLimit') || '20', 10);
    return Number.isNaN(saved) ? 20 : saved;
  });
  const [expandedId, setExpandedId] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(() => {
    const saved = parseInt(localStorage.getItem('pageLimit') || '20', 10);
    return Number.isNaN(saved) ? 20 : saved;
  });
  const [retryKey, setRetryKey] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [paginationByKey, setPaginationByKey] = useState({});
  let clientId = window.location.pathname.slice(1);

  if (clientId) {
    localStorage.setItem('clientId', clientId);
  } else {
    clientId = localStorage.getItem('clientId') || '';
  }

  const apiUrl = useMemo(() => {
    // keep URL stable; we send page/limit in body
    return (process.env.REACT_APP_API_BASE_URL || '') + '/api/track-package';
  }, []);

  const canFetch = Boolean(clientId);
  const { isLoading, data, error } = useFetch(
    apiUrl,
    {
      method: 'POST',
      // include pagination in body
      body: JSON.stringify({ clientId, page, limit }),
      headers: {
        'content-type': 'application/json'
      },
      credentials: 'include',
      // wait until clientId is available, allow manual retries
      depends: [canFetch, retryKey]
    }
  );

  useEffect(() => {
    if (!isLoading && !error && data) {
      const shaped = viewModel.shape(data);
      const actualPage = (data && data.pagination && data.pagination.page) ? data.pagination.page : page;
      const actualLimit = (data && data.pagination && data.pagination.limit) ? data.pagination.limit : limit;
      const key = `${actualLimit}:${actualPage}`;
      setOrdersByKey(prev => ({ ...prev, [key]: shaped }));
      setPagination(data.pagination || null);
      setPaginationByKey(prev => ({ ...prev, [key]: data.pagination || null }));
      // Only switch the visible (shown) page/limit if this response matches the current requested pair
      if (actualLimit === limit && actualPage === page) {
        setShownPage(page);
        setShownLimit(limit);
      }
    }
  }, [isLoading, error, data, page, limit]);

  // Helper to change pages while keeping current page visible until data for the new page arrives
  const setPageSafe = (nextPage) => {
    setPage(nextPage);
    // If we already have cached data for that (limit,page), switch immediately for snappy UX
    const key = `${limit}:${nextPage}`;
    if (ordersByKey[key]) {
      setShownPage(nextPage);
      setShownLimit(limit);
    }
  };

  // When limit changes, go to page 1 and persist choice. Keep current page visible until new data arrives.
  useEffect(() => {
    setPage(1);
    localStorage.setItem('pageLimit', String(limit));
  }, [limit]);

  // Reset expanded card when the visible page changes to avoid stale expansion state
  useEffect(() => {
    setExpandedId(null);
  }, [shownPage]);

  const retryFetch = () => {
    if (!canFetch) {
      return;
    }
    setRetryKey(prev => prev + 1);
  };

  return {
    isLoading,
    hasError: Boolean(error),
    error,
    // data for the currently shown page (could be stale while new page is loading)
    shownOrders: ordersByKey[`${shownLimit}:${shownPage}`] || [],
    expandedId,
    setExpandedId,
    page,
    shownPage,
    shownLimit,
    setPage: setPageSafe,
    pagination,
    paginationByKey,
    limit,
    setLimit,
    retryFetch
  };
}

function Orders() {
  const { shownOrders, isLoading, hasError, expandedId, setExpandedId, shownPage, shownLimit, setPage, pagination, paginationByKey, retryFetch } =
    useOrders();
  // Use the pagination metadata that corresponds exactly to the currently shown content
  const keyForShown = `${shownLimit}:${shownPage}`;
  const displayedPagination = paginationByKey[keyForShown] || pagination;
  const hasOrders = Boolean(shownOrders && shownOrders.length > 0);
  const shouldShowSpinner = isLoading && !hasOrders;
  const shouldShowEmptyState = !isLoading && !hasError && !hasOrders;

  return (
    <Wrapper>
      {hasOrders ? (
        <ul className="orders">
          { 
          shownOrders.map(order => (
            <Order
              key={order.id}
              order={order}
              isExpanded={expandedId === order.id}
              onExpand={id => setExpandedId(id)}
            />
          ))}
        </ul>
      ) : shouldShowSpinner ? (
        <span className="spinner">Получение данных…</span>
      ) : shouldShowEmptyState ? (
        <div className="status-block status-empty">Заказов пока нет</div>
      ) : null}
      {hasError ? (
        <div className="status-block status-error">
          <strong>Не удалось получить данные</strong>
          <button className="retry-button" onClick={retryFetch} disabled={isLoading}>
            Повторить попытку
          </button>
        </div>
      ) : null}
      {displayedPagination && (displayedPagination.hasPrev || displayedPagination.hasNext || displayedPagination.totalPages > 1) ? (
        <div className="pagination">
          <button
            className="btn"
            disabled={isLoading || !displayedPagination.hasPrev || displayedPagination.prevPage == null}
            onClick={() => (displayedPagination.prevPage != null) && setPage(displayedPagination.prevPage)}
          >
            « Назад
          </button>
        
          <button
            className="btn"
            disabled={isLoading || !displayedPagination.hasNext || displayedPagination.nextPage == null}
            onClick={() => (displayedPagination.nextPage != null) && setPage(displayedPagination.nextPage)}
          >
            Вперёд »
          </button>
        
        </div>
      ) : null}
    </Wrapper>
  );
}

export default Orders;
